/* @flow */
import cx from 'classnames';
import * as React from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';

import Chat from 'entities/Chat';
import EventTracking from 'lib/EventTracking';
import LayerTrigger from 'components/LayerTrigger';
import Tooltip from 'components/Tooltip';
import MessageListWelcome from './MessageListWelcome';
import ScrollableArea from 'components/ScrollableArea';

import MessagesList from './MessagesList';

import { extractListItems } from 'chat/lib/MessageHistory';

import type { MessageListItems } from 'chat/lib/MessageHistory';
import type { MessageType } from 'chat/lib/MessageInterfacePropType';
import type Group from 'entities/Group';

import './styles/MessageListArea.scss';

type Props = {
  chat?: Chat,
  group?: Group,
  threadUUID?: string,
  unreadCount?: number,
  messages: MessageType[],
  onScrollToBottom?: Function,
  loadMoreMessages?: Function,
  subgroupRole?: string,
  className?: string,
  disableReactionToggle?: boolean,
  scrollToBottom?: boolean,
  scrollEventKey?: string,
};

type State = {
  tooltip: boolean,
  items: MessageListItems,
  hasScrolled: boolean,
};

class MessageListArea extends React.PureComponent<Props, State> {
  static defaultProps = {
    scrollToBottom: true,
  };

  static childContextTypes = {
    threadUUID: PropTypes.string,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      tooltip: false,
      items: extractListItems(props.messages),
      hasScrolled: false,
    };
  }

  getChildContext() {
    const context = {};

    if (this.props.threadUUID) {
      context.threadUUID = this.props.threadUUID;
    }

    return context;
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.messages !== this.props.messages) {
      this.setState({
        items: extractListItems(nextProps.messages),
      });
    }
  }

  // TODO refactor this to avoid setState
  componentDidUpdate(prevProps: Props) {
    const unreads = this.props.unreadCount || 0;

    if (unreads > 0 && !this.isScrolledToBottom() && !this.state.tooltip) {
      this.refs.trigger.showLayer();
      this.setState({ tooltip: true }); // eslint-disable-line react/no-did-update-set-state
    }

    if (this.props.chat !== prevProps.chat) {
      this.scrollToBottom();
    }

    if (prevProps.messages.length === this.props.messages.length - 1) {
      this.handleReceivingNewMessage();
    }
  }

  detectScrolledToBottom = () => {
    if (this.isScrolledToBottom()) {
      if (this.props.onScrollToBottom) {
        this.props.onScrollToBottom();
      }
      this.setState({ tooltip: false });
      this.refs.trigger.handleDelayedHide();
    }
  };

  isScrolledToBottom = () => {
    return this.refs.scrollable && this.refs.scrollable.isScrolledToBottom(50);
  };

  scrollToBottom = () => {
    if (this.refs.scrollable) {
      this.refs.scrollable.scrollToBottom();
      this.detectScrolledToBottom();
    }
  };

  handleScroll = debounce(() => {
    if (!this.state.hasScrolled) {
      if (this.props.scrollEventKey) {
        EventTracking.trackKey(this.props.scrollEventKey);
      }
      this.setState({ hasScrolled: true });
    }

    this.detectScrolledToBottom();
  }, 200);

  handleReceivingNewMessage() {
    if (this.isScrolledToBottom()) {
      this.scrollToBottom();
    }
  }

  renderTooltipLauncher() {
    const unreads = this.props.unreadCount || 0;

    const messageText =
      unreads === 1 ? 'You have 1 unread message' : 'You have ' + unreads + ' unread messages';

    const tooltip = (
      <Tooltip
        onClick={this.scrollToBottom}
        className={cx({
          'MessageListArea-tooltip': true,
          'MessageListArea-unread': this.state.tooltip,
          'MessageListArea-unreadFade': !this.state.tooltip,
        })}
        hideOnEscape={false}
        hideOnBlur={false}>
        {messageText}
      </Tooltip>
    );

    return (
      <LayerTrigger
        ref="trigger"
        layer={tooltip}
        trigger="manual"
        placement="top"
        alignment="middle">
        <div />
      </LayerTrigger>
    );
  }

  renderWelcome() {
    const { chat, group, subgroupRole } = this.props;

    return <MessageListWelcome chat={chat} group={group} subgroupRole={subgroupRole} />;
  }

  render() {
    const { chat, group, threadUUID, subgroupRole } = this.props;
    return (
      <div className={cx('MessageListArea', this.props.className)}>
        <ScrollableArea
          useBodyChildrenHeight
          scrollToBottom={this.props.scrollToBottom}
          ref="scrollable"
          onScroll={this.handleScroll}>
          {this.renderWelcome()}
          <MessagesList
            group={group}
            threadUUID={threadUUID}
            isGroupChat={chat ? chat.isGroupChat() : false}
            isUnsubscribed={chat ? chat.isUnsubscribed() : false}
            disableReactionToggle={this.props.disableReactionToggle}
            items={this.state.items}
            onGapVisible={this.props.loadMoreMessages}
            subgroupRole={subgroupRole}
          />
        </ScrollableArea>
        {this.renderTooltipLauncher()}
      </div>
    );
  }
}

export default MessageListArea;
